import { useCallback, useRef, useState } from 'react';

/**
 * @typedef {object} ApiFormFieldsToUpdateItem
 * @property {string} id - ID of the field to update
 * @property {string} value - Field value (e.g. contact id)
 * @property {string} displayedValue - Display label
 * @property {string | null} originalValue - Entity id to replace in values, or null to add
 */

/**
 * @typedef {(fieldsToUpdate: ApiFormFieldsToUpdateItem[]) => void} OnChildSuccessCallback
 */

/**
 * @typedef {object} FormPanelEntry
 * @property {string} id
 * @property {string} formUrl
 * @property {boolean} [isClosing]
 * @property {OnChildSuccessCallback} [onChildSuccess] - Called when this layer's form submits successfully
 */

let panelIdCounter = 0;

/**
 * Generate a unique panel id for scroll container / stacking.
 * @returns {string}
 */
function createPanelId() {
  panelIdCounter += 1;
  return `form-panel-${panelIdCounter}`;
}

/**
 * Hook to manage form side panel stack state.
 * @returns {{
 *   isFormPanelOpen: boolean,
 *   stack: FormPanelEntry[],
 *   activeFormUrl: string | null,
 *   activePanelId: string,
 *   stackDepth: number,
 *   openFormPanel: (formUrl: string, onChildSuccess?: OnChildSuccessCallback) => void,
 *   closeFormPanel: () => void,
 *   closeTopFormPanel: () => void,
 *   handleCloseAfterSuccess: (fieldsToUpdate?: ApiFormFieldsToUpdateItem[]) => void,
 *   completeCloseLayer: (layerId: string) => void,
 *   handleOpenModal: (formUrl: string) => void,
 *   handleCloseModal: () => void,
 *   isModalFormOpen: boolean,
 *   selectedFormUrl: string | null,
 * }}
 */
export function useFormPanelStack() {
  const [stack, setStack] = useState(/** @type {FormPanelEntry[]} */ ([]));
  const [isOpen, setIsOpen] = useState(false);
  const stackRef = useRef(stack);
  stackRef.current = stack;

  const markTopLayerClosing = useCallback(() => {
    setStack((prev) => {
      if (prev.length === 0) return prev;
      const topIndex = prev.length - 1;
      return prev.map((entry, index) => (
        index === topIndex ? { ...entry, isClosing: true } : entry
      ));
    });
  }, []);

  const openFormPanel = useCallback((formUrl, onChildSuccess) => {
    setStack((prev) => {
      if (prev.length === 0) {
        setIsOpen(true);
      }
      return [...prev, {
        id: createPanelId(),
        formUrl,
        isClosing: false,
        onChildSuccess,
      }];
    });
  }, []);

  const handleCloseAfterSuccess = useCallback((fieldsToUpdate) => {
    const topEntry = stackRef.current[stackRef.current.length - 1];
    if (fieldsToUpdate?.length && topEntry?.onChildSuccess) {
      topEntry.onChildSuccess(fieldsToUpdate);
    }
    markTopLayerClosing();
  }, [markTopLayerClosing]);

  const completeCloseLayer = useCallback((layerId) => {
    setStack((prev) => {
      const next = prev.filter((entry) => entry.id !== layerId);
      if (next.length === 0) {
        setIsOpen(false);
      }
      return next;
    });
  }, []);

  const closeFormPanel = useCallback(() => {
    if (!isOpen && stack.length === 0) return;
    markTopLayerClosing();
  }, [isOpen, stack.length, markTopLayerClosing]);

  const closeTopFormPanel = markTopLayerClosing;

  const topEntry = stack[stack.length - 1];
  const activeFormUrl = topEntry?.formUrl ?? null;
  const activePanelId = topEntry?.id ?? 'form-panel';
  const stackDepth = Math.max(0, stack.length - 1);

  return {
    isFormPanelOpen: isOpen,
    stack,
    activeFormUrl,
    activePanelId,
    stackDepth,
    openFormPanel,
    closeFormPanel,
    closeTopFormPanel,
    handleCloseAfterSuccess,
    completeCloseLayer,
    handleOpenModal: openFormPanel,
    handleCloseModal: closeFormPanel,
    isModalFormOpen: isOpen,
    selectedFormUrl: activeFormUrl,
  };
}
